/**
 * Skeleton component — Brand Book §2.18
 * bg-subtle base with left-to-right shimmer sweep
 * 1.5s linear infinite animation
 * Variants: text (multi-line), card, table-row, chart, circle
 */

import { clsx } from 'clsx';

export interface SkeletonProps {
  variant?: 'text' | 'card' | 'table-row' | 'chart' | 'circle' | 'block';
  width?: string;
  height?: string;
  lines?: number;
  className?: string;
}

const shimmerStyle: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(90deg, rgb(var(--bg-subtle)) 0%, rgb(var(--bg-muted)) 40%, rgb(var(--bg-subtle)) 80%)',
  backgroundSize: '200% 100%',
  animation: 'skeletonShimmer 1.5s linear infinite',
};

export function Skeleton({
  variant = 'text',
  width,
  height,
  lines = 1,
  className,
}: SkeletonProps) {
  if (variant === 'text') {
    return (
      <div className={clsx('space-y-2', className)}>
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className="h-3"
            style={{
              ...shimmerStyle,
              width: width || (i === lines - 1 && lines > 1 ? '60%' : '100%'),
            }}
          />
        ))}
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div className={clsx('border border-border p-3 space-y-3', className)}>
        <div className="h-4 w-1/3" style={shimmerStyle} />
        <div className="h-3 w-full" style={shimmerStyle} />
        <div className="h-3 w-2/3" style={shimmerStyle} />
      </div>
    );
  }

  if (variant === 'table-row') {
    return (
      <div className={clsx('flex items-center gap-3 py-2', className)}>
        <div className="h-3 flex-1" style={shimmerStyle} />
        <div className="h-3 w-20" style={shimmerStyle} />
        <div className="h-3 w-16" style={shimmerStyle} />
        <div className="h-3 w-24" style={shimmerStyle} />
      </div>
    );
  }

  if (variant === 'chart') {
    return (
      <div
        className={className}
        style={{
          ...shimmerStyle,
          width: width || '100%',
          height: height || '200px',
        }}
      />
    );
  }

  if (variant === 'circle') {
    return (
      <div
        className={clsx('rounded-full', className)}
        style={{
          ...shimmerStyle,
          width: width || '40px',
          height: height || width || '40px',
        }}
      />
    );
  }

  // Single shimmer rectangle sized by className (or width/height props). Unlike
  // the generic fallback it does NOT force an inline width/height, so utility
  // classes like `h-7 w-full` / `absolute inset-0` govern the size.
  if (variant === 'block') {
    return (
      <div
        className={className}
        style={{
          ...shimmerStyle,
          ...(width ? { width } : null),
          ...(height ? { height } : null),
        }}
      />
    );
  }

  // Generic rectangle
  return (
    <div
      className={className}
      style={{
        ...shimmerStyle,
        width: width || '100%',
        height: height || '16px',
      }}
    />
  );
}